import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, merge, shareReplay, tap, switchMap, startWith, pairwise } from 'rxjs/operators';
import { AuthenticationService } from '../core/services/authentication.service';
import { LogCategory, LogCoreService, LoggerService, LogType } from '../core/services/logger.service';
import { environment } from '../../environments/environment';
import { DisplayDetailsModalComponent } from '../core/modals/display-details/display-details.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

enum FacultyNavState {
    prev = -1,
    current = 0,
    next = 1
}


@Injectable()
export class FacultyService {

    public classesEndPoint = 'academics'; //use this to determine what endpoint getClasses returns
    public isLoading = false;
    public tcApproved = {};
    public pageNumber$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public approveYear$: Subject<string> = new Subject<string>();
    public state: FacultyNavState = FacultyNavState.current;
    public updatedSections$: Subject<any> = new Subject<any>();
    public uploadFile$: Subject<any> = new Subject<any>();
    public copyFile$: Subject<any> = new Subject<any>();

    
    private _sections$: Observable<any>;
    public get sections$(): Observable<any> {
        if (this._sections$ == null) {
            this._sections$ = this.http.get<any>(environment.apiUrl + '/faculty/self/currentsections').pipe(
                tap(_ => this.log("sections$", "sections$ reference created", LogCategory.SubscriptionData)),
                shareReplay(1)
            ) as Observable<any>;
        }
        return this._sections$;
    }


    public copySyllabi$ = this.copyFile$.pipe(
        tap((copyDetails) => {
            //console.log('copy details', copyDetails)
        }),
        switchMap((copyDetails) => {
            var syllabusData = {
                CourseNumber: copyDetails.courseNumber,
                Term: copyDetails.academicTerm,
                Year: copyDetails.academicYear,
                SectionIds: copyDetails.selectedSections
            }

            var url = `${environment.apiUrl}/faculty/syllabus/copy`

            return this.http.post<any>(url, syllabusData);           
        })
    );

    public uploadSyllabus$ = this.uploadFile$.pipe(
        tap((uploadFile) => {
            //console.log('uploadFile', uploadFile)
        }),
        switchMap((fileDetails) => {
            var url = `${environment.apiUrl}/faculty/syllabus/upload`
            const formData: FormData = new FormData();
            const data: File = fileDetails.file;
            formData.append('file', data, data.name);
            for (const id in fileDetails.selectedSections) {
                formData.append(`sectionIds[${id}]`, fileDetails.selectedSections[id].toString());
            }

            return this.http.post<any>(url, formData);            
        })
    );


    constructor(
        private http: HttpClient,
        private auth: AuthenticationService,
        private logger: LoggerService,
        private modalService: NgbModal,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreService, LogCoreService.Profile, category);
    }
    
    public getClasses$(pageNumber: number): Observable<any> {
        var url = `${environment.apiUrl}/faculty/self/${this.classesEndPoint}?AcademicYearAddend=${pageNumber}`       
        return this.http.get<any>(url);    
    }
    
    public classes$: Observable<any> = this.pageNumber$.pipe(
        startWith(0), //need this as pairwise only emits after the first value       
        pairwise(),
        switchMap(([origVal, newVal]) => {
            this.isLoading = true;
            if (newVal === 0 && origVal < -1)
                this.state = 1;
            else
                this.state = newVal - origVal;            
            return this.getClasses$(newVal)
        }),
        tap(() => { this.isLoading = false; }),
        shareReplay(1)
    );

    public approve$: Observable<any> = this.approveYear$.pipe(
        switchMap((approveYear) => {
            var url = `${environment.apiUrl}/faculty/self/teachingcredits/approve?academicYear=${approveYear}`
            this.isLoading = true;
            return this.http.put<any>(url, {});
        }),
        tap((result) => {
            this.isLoading = false;
            this.tcApproved = result;
        })
    )

    public previousYearClasses$: Observable<any> = this.auth.hasFeature("SyllabusCopy") ?
        this.getClasses$(this.pageNumber$.value - 1)
            .pipe(shareReplay(1))
        : of([]);

    


    public syllabusResultModal(result) {
        const modalRef = this.modalService.open(DisplayDetailsModalComponent);
        modalRef.componentInstance.title = '<span class="ml-1">Course Resources Update Results</span>';

        let body = '<ul>';
        for (let i = 0; i < result['Results'].length; i++) {
            body += '<li>';
            body += result['Results'][i]['UserNotes'];
            body += '</li>';
        }
        body += '</ul>';


        modalRef.componentInstance.body = body;
        modalRef.componentInstance.okButton = "Got it!";
    }

}
